import React, { useMemo } from 'react';
import { Controller, FormProvider, useWatch } from 'react-hook-form';
import { observer, inject } from 'mobx-react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { avFilesDeliveryApi } from '@availity/api-axios';

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CloseIcon,
  Datepicker,
  FileSelector2,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  HelpCircleIcon,
  IconButton,
  List,
  ListItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@availity/element';

import CommonStore from '../../../Common/stores/CommonStore';
import ProviderStore from '../../../Common/stores/ProviderStore';

const today = dayjs();

/**
 * Return field properties for the option
 */

const extractAdditionalAttributesForOption = (item, optionText) => {
  const result = {};

  // Extract custom error text
  const additionalTextType = (item?.code || []).find(
    (codeObj) => codeObj.system === 'additionalTextType' && codeObj.display === optionText && codeObj.code
  );
  result.additionalTextType = additionalTextType?.code || 'string';

  // Extract future date validation message
  const additionalTextDescriptionList = (item?.code || []).filter(
    (codeObj) => codeObj.system === 'additionalTextDescription' && codeObj.display === optionText && codeObj.code
  );
  result.additionalTextDescriptionList =
    additionalTextDescriptionList.length > 0 ? additionalTextDescriptionList : null;

  return result;
};

/**
 * Format phone number with dashes (xxx-xxx-xxxx)
 */
const formatPhoneNumber = (value) => {
  if (!value) return value;
  
  // Remove all non-digit characters
  const phoneNumber = value.replaceAll(/\D/g, '');
  
  // Format based on length
  const phoneNumberLength = phoneNumber.length;
  
  if (phoneNumberLength < 4) return phoneNumber;
  
  if (phoneNumberLength < 7) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  }
  
  return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

/**
 * Check if field is a phone number field based on linkId
 */
const isPhoneNumberField = (linkId) => {
  return linkId && (
    linkId.includes('phone') || 
    linkId.includes('Phone') ||
    linkId === 'mycare_alleged_person_phone' ||
    linkId === 'mycare_person_info_1_phone' ||
    linkId === 'mycare_person_info_2_phone'
  );
};

/**
 * ItemRenderer:
 * - Renders one FHIR Questionnaire item (recursively for groups)
 * - Maps "string" + answerOption + itemControl to Select or Radio
 * - Maps "string" without options to a text field
 * - Maps "date" to a date input
 * - Honors required, readOnly, regex, minLength, and display children
 */

const ItemRenderer = ({
  item,
  formMethods,
  enableWhenOk,
  repeatCount,
  groupCounts,
  handleAddGroup,
  handleRemoveGroup,
  clearValues,
  clearItemValues,
  commonStore,
  providerStore,
  extractItemProperties,
  isPageGroup,
  getItemControl,
}) => {
  // BEGIN: Functional Component

  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors },
    clearErrors,
  } = formMethods;

  const { linkId, text, type, required, readOnly, repeats, maxLength } = item;

  const {
    regex,
    minLen,
    customErrorMsg,
    tooltiptext,
    uiProps,
    additionalTextType,
    additionalTextDescriptionList,
    endDateRequired,
    placeholder,
    inline,
    showCharCount,
    startDateText,
    endDateText,
    noFutureDateErrorMsg,
    noPastDateErrorMsg,
    maxBackDays,
    numberOfRows,
    requiredErrorMsg,
    minLenCustomErrorMsg,
    maxSelectionLimit,
    maxSelectionErrorMsg,
    mutuallyExclusiveOption,
    minDateFromField,
    minDateFromFieldErrorMsg,
    disabledWhen
  } = extractItemProperties(item, repeatCount);

  // Evaluate disabledWhen condition ("fieldId=expected value") to disable this field
  const disabledMatch = useMemo(() => {
    if (!disabledWhen) return null;
    const idx = disabledWhen.indexOf('=');
    return [disabledWhen.slice(0, idx).trim(), disabledWhen.slice(idx + 1).trim()];
  }, [disabledWhen]);
  const disabledSourceValue = useWatch({ control, name: disabledMatch ? disabledMatch[0] : linkId });
  const isDisabled = !!disabledMatch &&
    (Array.isArray(disabledSourceValue)
      ? disabledSourceValue.includes(disabledMatch[1])
      : disabledSourceValue === disabledMatch[1]);

  const effectiveReadOnly = readOnly || isDisabled;

  const size = 'small'; // consistent compact inputs

  const controlType = getItemControl(item);

  // RHF validation rules built from FHIR constraints

  const rules = {
    required: required ? requiredErrorMsg || `${text || 'Field'} is required` : false,
    validate: (val) => {
      if (val == null || val === '') return true; // let "required" rule catch empties
      const s = String(val);
      if (regex && !regex.test(s)) return customErrorMsg || 'Invalid field format';
      if (minLen && s.length < minLen)
        return minLenCustomErrorMsg || `${text || 'Field'} must be at least ${minLen} characters`;
      return true;
    },
  };

  const checkBoxRules = {
    validate: (value) => {
      if (required && (!value || value.length === 0)) {
        return requiredErrorMsg || `${text || 'Field'} is required`;
      }
      if (maxSelectionLimit && value && value.length > maxSelectionLimit) {
        return maxSelectionErrorMsg || `You can select a maximum of ${maxSelectionLimit} options`;
      }
      return true;
    },
  };

  const fileSelectorRules = {
    required: required ? requiredErrorMsg || `${text || 'Field'} is required` : false,

    validate: (UploadedFilesArr) => {
      UploadedFilesArr = UploadedFilesArr || []; // Ensure UploadedFilesArr is an array

      for (const uploadFileObject of UploadedFilesArr) {
        if (uploadFileObject.error || uploadFileObject.errorMessage) {
          return 'One or more file uploads have failed, please try uploading again!';
        }
      }
      return true;
    },
  };

  const createStartDateRules = ({
    required,
    noFutureDateErrorMsg,
    noPastDateErrorMsg,
    maxBackDays,
    uniqueKey,
    getValues,
    startDateText = 'Field',
    endDateText,
    minDateFromField,
    minDateFromFieldErrorMsg,
  }) => ({
    required: required ? requiredErrorMsg || `${startDateText} is required` : false,

    validate: (value) => {
      if (value) {
        const selectedDate = dayjs(value);

        if (!selectedDate.isValid()) {
          return `${startDateText} is invalid`;
        }

        // Only check for future date if value is a valid dayjs object
        if (selectedDate.isValid() && noFutureDateErrorMsg && selectedDate.isAfter(dayjs(), 'day')) return noFutureDateErrorMsg;

        // Only check for past date if value is a valid dayjs object
        if (selectedDate.isValid() && noPastDateErrorMsg && selectedDate.isBefore(dayjs(), 'day')) return noPastDateErrorMsg;

        // Check if date is older than maxBackDays from today
        if (selectedDate.isValid() && maxBackDays && selectedDate.isBefore(dayjs().subtract(maxBackDays, 'day'), 'day')) {
          return `Date cannot be more than ${Math.round(maxBackDays / 365)} year(s) in the past`;
        }

        if (uniqueKey) {
          const endDate = getValues(`${uniqueKey}-end`);
          if (endDate && selectedDate.isAfter(dayjs(endDate))) {
            return `${startDateText || 'Start date'} must be before ${endDateText || 'end date'}`;
          }
        }

        // Check min date against other field(s) (comma-separated; latest wins)
        if (minDateFromField) {
          let maxMinDate = null;
          for (const fieldId of minDateFromField.split(',').map((f) => f.trim())) {
            const minDateValue = getValues(fieldId);
            if (minDateValue && dayjs(minDateValue).isValid() && (!maxMinDate || dayjs(minDateValue).isAfter(maxMinDate))) {
              maxMinDate = dayjs(minDateValue);
            }
          }
          if (maxMinDate && selectedDate.isBefore(maxMinDate, 'day')) {
            return minDateFromFieldErrorMsg || `${startDateText} must be on or after ${maxMinDate.format('MM/DD/YYYY')}`;
          }
        }
      }

      return true;
    },
  });

  const createEndDateRules = ({
    required,
    noFutureDateErrorMsg,
    noPastDateErrorMsg,
    uniqueKey,
    getValues,
    endDateRequired,
    startDateText,
    endDateText,
  }) => ({
    required: endDateRequired && required ? `${requiredErrorMsg || endDateText || 'To date'} is required` : false,

    validate: (value) => {
      if (value) {
        const selectedDate = dayjs(value);
        const startDate = getValues(`${uniqueKey}-start`); // Access start date using uniqueKey

        // No future dates for end date
        if (endDateRequired && noFutureDateErrorMsg && selectedDate.isAfter(today)) return noFutureDateErrorMsg;

        // No past dates for end date
        if (endDateRequired && noPastDateErrorMsg && selectedDate.isBefore(today)) return noPastDateErrorMsg;

        if (startDate && selectedDate.isBefore(dayjs(startDate))) {
          return `${endDateText || 'End date'} must be after ${startDateText || 'start date'}`;
        }
      }

      return true;
    },
  });

  const isGroup_a_Page = isPageGroup(item);

  // Convert answerOption into a flat array of strings for select/radios
  const hasOptions = Array.isArray(item.answerOption) && item.answerOption.length > 0;
  const options = (item.answerOption || []).map(
    (o) => o.valueCoding?.code ?? o.valueCoding?.display ?? o.valueString ?? ''
  );

  const uniqueKey = repeatCount ? `${linkId}-${repeatCount}` : linkId; // Unique identifier

  const onUpload = (upload, linkId) => {
    const { brand, deliveryChannel, bucket, payerId, qocID, groupID, claimID, providerID } = commonStore;
    const { state } = brand;

    if (!upload) return false;
    commonStore.handleChangeCommonStore('blocking', true);

    if (upload.errorMessage) {
      commonStore.handleChangeCommonStore('blocking', false);
    }

    upload.onSuccess.push(async () => {
      const data = {
        deliveries: [
          state === 'OH'
            ? {
                deliveryChannel,
                fileURI: upload.s3References ? upload.s3References[0] : '',
                metadata: {
                  market: state,
                  source: 'Availity',
                  memberID: getValues('memberID'),
                  memberFirstName: getValues('memberFirstName'),
                  memberLastName: getValues('memberLastName'),
                  bucket,
                  payerId,
                },
              }
            : {
                deliveryChannel,
                fileURI: upload.s3References ? upload.s3References[0] : '',
                metadata: {
                  market: state,
                  source: 'Availity',
                  memberID: getValues('memberID').slice(3, getValues('memberID').length),
                  memberFirstName: getValues('memberFirstName'),
                  memberLastName: getValues('memberLastName'),
                  bucket,
                  qocID,
                  groupID,
                  claimID,
                  providerID,
                  payerId,
                },
              },
        ],
      };
      const config = {
        customerId: upload.upload.options.headers['X-Availity-Customer-ID'],
        clientId: upload.upload.options.headers['X-Client-ID'],
        pollingIntervals: [
          1e3, 2e3, 5e3, 5e3, 5e3, 5e3, 5e3, 5e3, 5e3, 5e3, 5e3, 5e3, 5e3, 1e4, 1e4, 1e4, 1e4, 1e4, 1e4, 1e4, 1e4, 1e4,
          1e4, 1e4, 1e4,
        ],
      };

      const uploadedFiles = getValues(linkId) || [];
      try {
        const response = await avFilesDeliveryApi.uploadFilesDelivery(data, config);
        if (response.status === 200 && response.data.deliveries[0].errors.length === 0) {
          for (const file of uploadedFiles) {
            if (file.id === upload.id) {
              const { metadata } = response.data.deliveries[0] || {};
              if (metadata) {
                const { filename } = metadata;
                file.metadata = metadata;
                if (filename) {
                  file.metadata.docType = filename.split('.').pop();
                }
                file.metadata.bucket = 'Macess';
              }
            }
          }
        } else {
          for (const file of uploadedFiles) {
            if (file.id === upload.id) {
              file.errorMessage = `System Unavailable. Please try again later.`;
            }
          }
        }
      } catch {
        for (const file of uploadedFiles) {
          if (file.id === upload.id) {
            file.errorMessage = `System Unavailable. Please try again later.`;
          }
        }
      } finally {
        setValue(linkId, uploadedFiles);
        commonStore.handleChangeCommonStore('blocking', false);
      }
    });

    upload.onError.push(() => {
      commonStore.handleChangeCommonStore('blocking', false);
    });
    return true;
  };

  /**
   * Return additional details for radio and checkbox options if present
   */

  const renderAdditionalTextForOptions = ({
    additionalTextType,
    additionalTextDescriptionList,
    uniqueKey,
    isGroup_a_Page,
  }) => {
    if (!additionalTextDescriptionList?.length) return null;
    const variantType = isGroup_a_Page ? 'h6' : 'p'; // precise condition
    const renderList = (listType) => (
      <List
        sx={{
          ml: '20px',
          mt: '-1.1rem',
          listStyleType: listType === 'unorderedList' ? 'disc' : 'decimal',
          listStylePosition: 'inside',
          '.MuiListItem-root': {
            display: 'list-item',
          },
        }}
        disablePadding
      >
        {additionalTextDescriptionList.map(({ code }) => (
          <ListItem key={code} id={`list-text-${code}`} disableGutters disablePadding divider={false}>
            {code}
          </ListItem>
        ))}
      </List>
    );

    // Helper to render plain text
    const renderText = () => (
      <Box
        id={`additional-description-${uniqueKey}`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {additionalTextDescriptionList.map(({ code }, index) => (
          <Typography
            variant={variantType}
            key={code}
            id={`typography-text-${code}`}
            sx={{ mt: index > 0 ? '0.5rem' : 0 }}
          >
            {code}
          </Typography>
        ))}
      </Box>
    );

    // Render based on the type
    return (
      <Box id={`additional-text-list-wrapper-${uniqueKey}`}>
        {additionalTextType === 'unorderedList' || additionalTextType === 'orderedList'
          ? renderList(additionalTextType)
          : renderText()}
      </Box>
    );
  };

  switch (type) {
    case 'display': {
      // A pure display node (help text / section text)
      return (
        <Box sx={{ m: 0, width: '100%' }} className={uiProps.className} id={`box-${linkId}`}>
          {text && (
            <Typography
              variant={isGroup_a_Page ? 'h2' : 'h5'}
              sx={{ ...uiProps.sx, m: isGroup_a_Page ? '1.25rem 0' : '0 0 1.25rem 0' }}
              id={`typography-${uniqueKey}`}
            >
              {text}
              {repeats ? ` ${Math.max(repeatCount + 1, 1)}` : ''}
            </Typography>
          )}
          {additionalTextDescriptionList ? (
            <Box id={`page-description-${item.linkId}`} sx={{ m: isGroup_a_Page ? '1.25rem 0' : 0 }}>
              {renderAdditionalTextForOptions({
                additionalTextType,
                additionalTextDescriptionList,
                uniqueKey: linkId,
                isGroup_a_Page,
              })}
            </Box>
          ) : null}
        </Box>
      );
    }

    case 'string': {
      // If the "string" item also has answerOption, render as select/radio based on itemControl
      if (hasOptions) {
        if (controlType === 'drop-down') {
          return (
            <Controller
              name={uniqueKey}
              control={control}
              disabled={isDisabled}
              rules={rules}
              render={({ field, fieldState }) => (
                <FormControl
                  id={`form-control-${uniqueKey}`}
                  fullWidth
                  error={!!fieldState.error}
                  className={uiProps.className}
                  sx={uiProps.sx}
                >
                  <FormLabel id={`form-control-label-${uniqueKey}`}>
                    <Box id={`form-label-box-${uniqueKey}`} display="flex" alignItems="center">
                      {text ? (
                        <Typography component="span" id={`text-span-for-${uniqueKey}`}>{`${text}${
                          required ? ' *' : ''
                        }`}</Typography>
                      ) : (
                        ''
                      )}
                      {tooltiptext && (
                        <Tooltip id={`tooltip-${uniqueKey}`} title={tooltiptext} placement="right">
                          <IconButton
                            id={`tooltip-icon-${uniqueKey}`}
                            style={{ padding: 0, marginLeft: 8 }}
                            tabIndex={-1}
                            aria-hidden="true"
                          >
                            <HelpCircleIcon id={`tooltip-help-icon-${uniqueKey}`} fontSize="xxsmall" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </FormLabel>
                  <Autocomplete
                    {...field}
                    id={uniqueKey}
                    disabled={!!effectiveReadOnly}
                    options={options} // Pass the options array
                    getOptionLabel={(option) => option || ''} // Handle empty or undefined options
                    value={field.value || null} // Ensure value is not undefined
                    onChange={(_, newValue) => field.onChange(newValue)} // Update the value
                    onBlur={() => {
                      trigger(uniqueKey);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size={size}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        placeholder={placeholder || 'Select'}
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          );
        }

        if (controlType === 'checkbox' || controlType === 'check-box') {
          return (
            <FormControl component="fieldset" id={uniqueKey} name={uniqueKey} error={!!errors[uniqueKey]}>
              <FormLabel component="legend" id={`form-control-label-${uniqueKey}`}>
                <Box id={`form-label-box-${uniqueKey}`} display="flex" alignItems="center">
                  {text ? (
                    <Typography component="span" id={`text-span-for-${uniqueKey}`}>{`${text}${
                      required ? ' *' : ''
                    }`}</Typography>
                  ) : (
                    ''
                  )}
                  {tooltiptext && (
                    <Tooltip id={`tooltip-${uniqueKey}`} title={tooltiptext} placement="right">
                      <IconButton
                        id={`tooltip-icon-${uniqueKey}`}
                        style={{ padding: 0, marginLeft: 8 }}
                        tabIndex={-1}
                        aria-hidden="true"
                      >
                        <HelpCircleIcon id={`tooltip-help-icon-${uniqueKey}`} fontSize="xxsmall" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </FormLabel>
              <FormGroup
                id={`form-control-group-${uniqueKey}`}
                sx={{
                  flexDirection: inline ? 'row' : 'column',
                }}
              >
                <Controller
                  name={uniqueKey}
                  control={control}
                  disabled={isDisabled}
                  rules={checkBoxRules}
                  render={({ field: { value = [], onChange } }) =>
                    options.map((opt) => {
                      const { additionalTextType, additionalTextDescriptionList } =
                        extractAdditionalAttributesForOption(item, opt);

                      // Disable and uncheck the exclusive option when any other option is selected
                      const isExclusiveOptDisabled =
                        mutuallyExclusiveOption &&
                        opt === mutuallyExclusiveOption &&
                        value.length > 0 &&
                        !value.every((v) => v === mutuallyExclusiveOption);
                      const isOptDisabled = !!effectiveReadOnly || !!isExclusiveOptDisabled;

                      return (
                        <Box
                          id={`controller-div--${uniqueKey}`}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column', // Stack children vertically
                            gap: additionalTextType || additionalTextDescriptionList?.length > 0 ? '0.5rem' : '0', // Add spacing between FormControlLabel and Box
                          }}
                        >
                          <FormControlLabel
                            key={opt}
                            id={`form-control-label-${opt}`}
                            name={`form-control-label-${opt}`}
                            control={
                              <Checkbox
                                checked={value.includes(opt)}
                                disabled={isOptDisabled}
                                onChange={() => {
                                  let newValue = value.includes(opt)
                                    ? value.filter((v) => v !== opt)
                                    : [...value, opt];
                                  // If a non-exclusive option is checked, auto-uncheck the exclusive option
                                  if (mutuallyExclusiveOption && opt !== mutuallyExclusiveOption && !value.includes(opt)) {
                                    newValue = newValue.filter((v) => v !== mutuallyExclusiveOption);
                                  }
                                  onChange(newValue); // Trigger validation on change
                                  trigger(uniqueKey); // Manually trigger validation
                                }}
                              />
                            }
                            label={opt}
                          />
                          {additionalTextDescriptionList ? (
                            <Box
                              id={`additional-description-${uniqueKey}`}
                              sx={{
                                margin: additionalTextType === 'string' ? '0 1.25rem 1.25rem 2.5rem' : '1.25rem',
                              }}
                            >
                              {renderAdditionalTextForOptions({
                                additionalTextType,
                                additionalTextDescriptionList,
                                uniqueKey,
                              })}
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })
                  }
                />
              </FormGroup>
              {errors[uniqueKey] && (
                <FormHelperText id={`form-control-helper-text-${uniqueKey}`}>
                  {errors[uniqueKey].message}
                </FormHelperText>
              )}
            </FormControl>
          );
        }

        // Fallback for string-with-options: radio group
        return (
          <Controller
            name={uniqueKey} // Modified to use uniqueKey
            control={control}
            disabled={isDisabled}
            rules={rules}
            render={({ field, fieldState }) => (
              <FormControl
                id={uniqueKey} // Modified to use uniqueKey
                name={uniqueKey}
                disabled={!!effectiveReadOnly}
                error={!!fieldState.error}
                className={uiProps.className}
                sx={uiProps.sx}
              >
                <FormLabel id={`form-label-${uniqueKey}`}>
                  <Box id={`form-label-box-${uniqueKey}`} display="flex" alignItems="center">
                    {text ? (
                      <Typography component="span" id={`text-span-for-${uniqueKey}`}>{`${text}${
                        required ? ' *' : ''
                      }`}</Typography>
                    ) : (
                      ''
                    )}
                    {tooltiptext && (
                      <Tooltip id={`tooltip-${uniqueKey}`} title={tooltiptext} placement="right">
                        <IconButton
                          id={`tooltip-icon-${uniqueKey}`}
                          style={{ padding: 0, marginLeft: 8 }}
                          tabIndex={-1}
                          aria-hidden="true"
                        >
                          <HelpCircleIcon id={`tooltip-help-icon-${uniqueKey}`} fontSize="xxsmall" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </FormLabel>

                <RadioGroup {...field} row={inline} aria-labelledby={`form-label-${uniqueKey}`}>
                  {options.map((opt) => {
                    const { additionalTextType, additionalTextDescriptionList } = extractAdditionalAttributesForOption(
                      item,
                      opt
                    );

                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column', // Stack children vertically
                          gap: additionalTextType || additionalTextDescriptionList?.length > 0 ? '0.5rem' : '0', // Add spacing between FormControlLabel and Box
                        }}
                      >
                        <FormControlLabel
                          key={opt}
                          value={opt}
                          control={<Radio size={size} id={`radio-${uniqueKey}-${opt}`} />} // Modified to use uniqueKey
                          label={opt}
                        />
                        {additionalTextDescriptionList ? (
                          <Box
                            id={`additional-description-${uniqueKey}`}
                            sx={{
                              margin: additionalTextType === 'string' ? '0 1.25rem 1.25rem 2.5rem' : '1.25rem',
                            }}
                          >
                            {renderAdditionalTextForOptions({
                              additionalTextType,
                              additionalTextDescriptionList,
                              uniqueKey,
                            })}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </RadioGroup>
                {errors[uniqueKey] && (
                  <FormHelperText id={`form-control-helper-text-${uniqueKey}`}>
                    {errors[uniqueKey].message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        );
      }

      if (controlType === 'dateRange') {
        // Date range input (start date and end date)
        return (
          <>
            {text ? (
              <Box id={`form-label-box-${uniqueKey}`} display="flex" alignItems="center" sx={{ mb: '3px' }}>
                {text ? (
                  <Typography component="span" id={`text-span-for-${uniqueKey}`}>{`${text}${
                    required ? ' *' : ''
                  }`}</Typography>
                ) : (
                  ''
                )}
                {tooltiptext && (
                  <Tooltip id={`tooltip-${uniqueKey}`} title={tooltiptext} placement="right">
                    <IconButton
                      id={`tooltip-icon-${uniqueKey}`}
                      style={{ padding: 0, marginLeft: 8 }}
                      tabIndex={-1}
                      aria-hidden="true"
                    >
                      <HelpCircleIcon id={`tooltip-help-icon-${uniqueKey}`} fontSize="xxsmall" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ) : (
              ''
            )}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs="auto">
                <Box
                  id={`div-for-start-date-${uniqueKey}`}
                  sx={{
                    '.MuiInputBase-root': {
                      width: '240px',
                    },
                    marginRight: '1rem', // Add spacing between the two datepickers
                  }}
                >
                  <Controller
                    name={`${uniqueKey}-start`}
                    control={control}
                    disabled={isDisabled}
                    rules={createStartDateRules({
                      required,
                      noFutureDateErrorMsg,
                      noPastDateErrorMsg,
                      maxBackDays,
                      uniqueKey,
                      getValues,
                      startDateText,
                      endDateText,
                    })}
                    render={({ field, fieldState }) => (
                      <Datepicker
                        {...field}
                        size={size}
                        id={`text-field-date-start-${uniqueKey}`}
                        label={startDateText || 'From'}
                        required={!!required}
                        disabled={!!effectiveReadOnly}
                        errorMessage={fieldState.error?.message}
                        isClearable
                        maxDate={getValues(`${uniqueKey}-end`) ?? undefined}
                        shouldDisableDate={(date) => {
                          const isPastDateDisabled = noPastDateErrorMsg ? date.isBefore(dayjs(), 'day') : false;
                          const isFutureDateDisabled = noFutureDateErrorMsg ? date.isAfter(dayjs(), 'day') : false;
                          const isBeforeMaxBack = maxBackDays ? date.isBefore(dayjs().subtract(maxBackDays, 'day'), 'day') : false;
                          return isPastDateDisabled || isFutureDateDisabled || isBeforeMaxBack;
                        }}
                        FieldProps={{
                          placeholder: 'MM/DD/YYYY',
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message,
                          onBlur: () => {
                            trigger(`${uniqueKey}-start`);
                          },
                        }}
                        onChange={(date) => {
                          field.onChange(date);
                          trigger(`${uniqueKey}-start`);
                        }}
                        onBlur={() => {
                          trigger(`${uniqueKey}-start`);
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs="auto">
                <Box
                  id={`div-for-end-date-${uniqueKey}`}
                  sx={{
                    '.MuiInputBase-root': {
                      width: '240px',
                    },
                  }}
                >
                  <Controller
                    name={`${uniqueKey}-end`}
                    control={control}
                    disabled={isDisabled}
                    rules={createEndDateRules({
                      required,
                      noFutureDateErrorMsg,
                      noPastDateErrorMsg,
                      uniqueKey,
                      getValues,
                      endDateRequired,
                      startDateText,
                      endDateText,
                    })}
                    render={({ field, fieldState }) => (
                      <Datepicker
                        {...field}
                        size={size}
                        id={`datepicker-end-${uniqueKey}`}
                        label={endDateText || 'To'}
                        required={!!required}
                        disabled={!!effectiveReadOnly}
                        errorMessage={fieldState.error?.message}
                        isClearable
                        minDate={getValues(`${uniqueKey}-start`) ?? undefined}
                        shouldDisableDate={(date) => {
                          const isPastDateDisabled = noPastDateErrorMsg ? date.isBefore(dayjs(), 'day') : false;
                          const isFutureDateDisabled = noFutureDateErrorMsg ? date.isAfter(dayjs(), 'day') : false;
                          const isBeforeMaxBack = maxBackDays ? date.isBefore(dayjs().subtract(maxBackDays, 'day'), 'day') : false;
                          return isPastDateDisabled || isFutureDateDisabled || isBeforeMaxBack;
                        }}
                        FieldProps={{
                          placeholder: 'MM/DD/YYYY',
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message,
                          onBlur: () => {
                            trigger(`${uniqueKey}-end`);
                          },
                        }}
                        onChange={(date) => {
                          field.onChange(date);
                          trigger(`${uniqueKey}-end`);
                        }}
                        onBlur={() => {
                          trigger(`${uniqueKey}-end`);
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </>
        );
      }

      if (controlType === 'fileSelector') {
        const { fileSelectorProps, fieldDescriptionArr } = extractItemProperties(item);

        return (
          <Box
            sx={{
              '& .MuiAlert-root': { margin: '1.25rem 0' },
              '& .MuiGrid2-root': {
                '& h6': { display: 'none' },
                '& p': { display: 'none' },
              },
            }}
          >
            <FormProvider {...formMethods}>
              <Controller
                name={uniqueKey}
                control={control}
                disabled={isDisabled}
                rules={fileSelectorRules}
                render={({ field, fieldState }) => (
                  <FormControl component="fieldset" id={`form-control-${uniqueKey}`} error={!!fieldState.error}>
                    <FormLabel component="legend" id={`form-control-label-${uniqueKey}`}>
                      <Box id={`form-label-box-${uniqueKey}`} display="flex" alignItems="center">
                        {text ? (
                          <Typography component="span" id={`text-span-for-${uniqueKey}`}>{`${text}${
                            required ? ' *' : ''
                          }`}</Typography>
                        ) : (
                          ''
                        )}
                        {tooltiptext && (
                          <Tooltip id={`tooltip-${uniqueKey}`} title={tooltiptext} placement="right">
                            <IconButton
                              id={`tooltip-icon-${uniqueKey}`}
                              style={{ padding: 0, marginLeft: 8 }}
                              tabIndex={-1}
                              aria-hidden="true"
                            >
                              <HelpCircleIcon id={`tooltip-help-icon-${uniqueKey}`} fontSize="xxsmall" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </FormLabel>
                    <Box id={`form-control-description-${uniqueKey}`}>
                      {(() => {
                        const boxes = []; // Initialize an array to store the JSX elements
                        for (const fieldDescriptionObj of fieldDescriptionArr) {
                          const generatedKey = `${uniqueKey}-${Math.random()}`; // Generate a unique key dynamically
                          boxes.push(
                            <Box
                              key={generatedKey}
                              id={`additional-description-${generatedKey}`}
                              sx={{ mb: '0.625rem' }}
                            >
                              {fieldDescriptionObj.code}
                            </Box>
                          );
                        }
                        return boxes; // Return the array of JSX elements
                      })()}
                    </Box>
                    <FileSelector2
                      {...field}
                      id={uniqueKey}
                      name={uniqueKey}
                      customerId={providerStore?.selectedOrganization?.customerId || ''}
                      maxSize={10485760}
                      enableDropArea={false}
                      multiple={false}
                      uploadOptions={{
                        retryDelays: [],
                        onPreStart: [(upload) => onUpload(upload, uniqueKey)],
                        onSuccess: () => trigger(uniqueKey),
                        onError: () => trigger(uniqueKey),
                        maxSize: fileSelectorProps?.maxSize || 10485760,
                      }}
                      onChange={() => {
                        trigger(uniqueKey);
                      }}
                      isCloud // remove this prop to replicate successful upload scenario using mock
                      {...fileSelectorProps}
                    />
                    {fieldState.error && (
                      <FormHelperText id={`form-control-helper-text-${uniqueKey}`}>
                        {fieldState.error?.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </FormProvider>
          </Box>
        );
      }

      return (
        <Controller
          name={uniqueKey}
          control={control}
          disabled={isDisabled}
          rules={rules}
          render={({ field, fieldState }) => (
            <Box className={uiProps.className} sx={uiProps.sx} id={`box-for-text-field-${uniqueKey}`}>
              <FormControl fullWidth error={!!fieldState.error}>
                <FormLabel id={`form-label-${uniqueKey}`} component="legend">
                  <Box id={`form-label-box-${uniqueKey}`} display="flex" alignItems="center">
                    {text ? (
                      <Typography component="span" id={`text-span-for-${uniqueKey}`}>{`${text}${
                        required ? ' *' : ''
                      }`}</Typography>
                    ) : (
                      ''
                    )}
                    {tooltiptext && (
                      <Tooltip id={`tooltip-${uniqueKey}`} title={tooltiptext} placement="right">
                        <IconButton
                          id={`tooltip-icon-${uniqueKey}`}
                          style={{ padding: 0, marginLeft: 8 }}
                          tabIndex={-1}
                          aria-hidden="true"
                        >
                          <HelpCircleIcon id={`tooltip-help-icon-${uniqueKey}`} fontSize="xxsmall" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </FormLabel>
                <TextField
                  {...field}
                  id={`text-field-${uniqueKey}`} // Unique ID for TextField using repeatCount
                  required={!!required}
                  error={!!fieldState.error}
                  helperText={fieldState.error ? fieldState.error.message : undefined} // Conditionally render helperText
                  onChange={(e) => {
                    const { value } = e.target;
                    // Apply phone formatting if this is a phone number field
                    if (isPhoneNumberField(linkId)) {
                      const formattedValue = formatPhoneNumber(value);
                      field.onChange(formattedValue);
                    } else {
                      field.onChange(value);
                    }
                  }}
                  onBlur={() => {
                    trigger(uniqueKey);
                  }}
                  size={size}
                  multiline={!!numberOfRows} // Add multiline if numberOfRows is defined
                  rows={numberOfRows || undefined} // Set rows to numberOfRows if defined
                  inputProps={{
                    maxLength: maxLength || undefined, // Ensure maxLength is passed correctly
                    'aria-label': `text-field-${uniqueKey}`, // Added aria-label for accessibility
                    'aria-labelledby': `form-label-${uniqueKey}`, // Added aria-labelledby for better accessibility
                    readOnly: effectiveReadOnly,
                  }}
                  placeholder={placeholder || ''} // Added placeholder
                />
                {maxLength && showCharCount && (
                  <Grid
                    container
                    justifyContent="flex-end"
                    id={`character-count-${uniqueKey}`}
                    sx={{ marginTop: '-20px' }}
                  >
                    <Typography
                      variant="caption"
                      align="right"
                      display="block"
                      sx={{ textAlign: 'right' }}
                      id={`character-count-span-${uniqueKey}`}
                    >
                      {field.value && field.value.length > 0
                        ? `${maxLength - field.value.length} of ${maxLength} characters left.`
                        : `${maxLength} characters max.`}
                    </Typography>
                  </Grid>
                )}
              </FormControl>
            </Box>
          )}
        />
      );
    }

    case 'date': {
      return (
        <Box
          className={uiProps.className}
          id={`box-for-date-field-${uniqueKey}`}
          sx={{
            '.MuiInputBase-root': {
              width: '240px',
            },
          }}
        >
          <Controller
            name={uniqueKey}
            control={control}
            disabled={isDisabled}
            rules={createStartDateRules({ required, noFutureDateErrorMsg, noPastDateErrorMsg, maxBackDays, getValues, minDateFromField, minDateFromFieldErrorMsg })}
            render={({ field, fieldState }) => (
              <Datepicker
                {...field}
                size={size}
                id={`date-field-date-${uniqueKey}`} // Modified to include repeatCount
                label={
                  <Box id={`form-label-box-${uniqueKey}`} display="flex" alignItems="center">
                    {text}
                    {required && (
                      <Typography id={`required-span-for-${uniqueKey}`} component="span" sx={{ marginLeft: '0.25rem' }}>
                        *
                      </Typography>
                    )}{' '}
                    {/* Add * as a suffix */}
                    {tooltiptext && (
                      <Tooltip id={`tooltip-${uniqueKey}`} title={tooltiptext} placement="right">
                        <IconButton
                          id={`tooltip-icon-${uniqueKey}`}
                          style={{ padding: 0, marginLeft: 8 }}
                          tabIndex={-1}
                          aria-hidden="true"
                        >
                          <HelpCircleIcon id={`tooltip-help-icon-${uniqueKey}`} fontSize="xxsmall" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                }
                required={!!required}
                disabled={!!effectiveReadOnly}
                errorMessage={fieldState.error?.message}
                isClearable
                shouldDisableDate={(date) => {
                  const now = dayjs();
                  const isPastDateDisabled = noPastDateErrorMsg ? date.isBefore(now, 'day') : false;
                  const isFutureDateDisabled = noFutureDateErrorMsg ? date.isAfter(now, 'day') : false;
                  const isBeforeMaxBack = maxBackDays ? date.isBefore(now.subtract(maxBackDays, 'day'), 'day') : false;
                  let isBeforeMinField = false;
                  if (minDateFromField) {
                    for (const fieldId of minDateFromField.split(',').map((f) => f.trim())) {
                      const minVal = getValues(fieldId);
                      if (minVal && dayjs(minVal).isValid() && date.isBefore(dayjs(minVal), 'day')) {
                        isBeforeMinField = true;
                        break;
                      }
                    }
                  }
                  return isPastDateDisabled || isFutureDateDisabled || isBeforeMaxBack || isBeforeMinField;
                }}
                FieldProps={{
                  placeholder: 'MM/DD/YYYY',
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message, // Use fieldState.error for helper text
                  onBlur: () => {
                    trigger(uniqueKey);
                  },
                }}
                onChange={(date) => {
                  field.onChange(date);
                  trigger(uniqueKey);
                }}
                onBlur={() => {
                  trigger(uniqueKey);
                }}
              />
            )}
          />
        </Box>
      );
    }

    case 'group': {
      // Recursively render child items inside a grid; grid sizes are driven by UI_EXT or defaulted
      const children = item.item || [];
      const parentId = item.linkId || '';
      const isGroup_a_Page = isPageGroup(item);
      const { additionalTextType, additionalTextDescriptionList, enableWhenArrFromCode } = extractItemProperties(item);

      if (!enableWhenOk(item.enableWhen, enableWhenArrFromCode, item.enableBehavior)) {
        clearItemValues(item, groupCounts, getValues, setValue, errors, clearErrors);
      }

      if (!enableWhenOk(item.enableWhen, enableWhenArrFromCode, item.enableBehavior)) return null; // skip disabled items

      return (
        <Box sx={{ m: 0, width: '100%' }} className={uiProps.className} id={`box-${item.linkId}`}>
          {item.text && (
            <Typography
              variant={isGroup_a_Page ? 'h2' : 'h5'}
              sx={{ ...uiProps.sx, m: isGroup_a_Page ? '1.25rem 0' : '0 0 1.25rem 0' }}
              id={`typography-${uniqueKey}`}
            >
              {item.text}
              {item.repeats ? ` ${Math.max(repeatCount + 1, 1)}` : ''}
            </Typography>
          )}
          {additionalTextDescriptionList ? (
            <Box id={`page-description-${item.linkId}`} sx={{ m: isGroup_a_Page ? '1.25rem 0' : '0 0 1.25rem 0' }}>
              {renderAdditionalTextForOptions({
                additionalTextType,
                additionalTextDescriptionList,
                uniqueKey: item.linkId,
                isGroup_a_Page,
              })}
            </Box>
          ) : null}
          <Grid
            container
            spacing={2}
            sx={uiProps.grid}
            id={isGroup_a_Page ? `grid-group-${item.linkId}` : `grid-${item.linkId}`}
          >
            {children.map((child) => {
              const { uiProps, isFieldToBeHidden, enableWhenArrFromCode, maxRepeatCount } =
                extractItemProperties(child);
              const xs = uiProps.grid?.xs ?? 12;
              const md = uiProps.grid?.md ?? (child.type === 'group' ? 12 : 6);
              // Hide if enableWhen resolves to false
              const visible = enableWhenOk(child.enableWhen, enableWhenArrFromCode, child.enableBehavior);

              const childType = child.type;
              const childLinkId = child.linkId;

              if (!enableWhenOk(child.enableWhen, enableWhenArrFromCode, child.enableBehavior)) {
                clearItemValues(child, groupCounts, getValues, setValue, errors, clearErrors);
              }

              if (!visible || isFieldToBeHidden) return null;

              const repeatCountVar = groupCounts[childLinkId] || 0;

              return (
                <Grid
                  key={childLinkId}
                  size={{ xs, md }}
                  sx={{ mb: childType === 'group' ? '1.25rem' : 0 }}
                  id={childType === 'group' ? `grid-item-group-${childLinkId}` : `grid-item-${childLinkId}`}
                >
                  {childType === 'group' && child.repeats ? (
                    <>
                      {Array.from({ length: repeatCountVar + 1 }, (_, value) => (
                        <Grid
                          container
                          spacing={2}
                          key={`${childLinkId}-repeat-${value}`}
                          sx={{ position: 'relative' }}
                          id={`${childLinkId}-repeat-${value}`}
                        >
                          <ItemRenderer
                            key={childLinkId + value}
                            repeatCount={value}
                            item={child}
                            formMethods={formMethods}
                            enableWhenOk={enableWhenOk}
                            groupCounts={groupCounts}
                            handleAddGroup={handleAddGroup}
                            handleRemoveGroup={handleRemoveGroup}
                            clearValues={clearValues}
                            clearItemValues={clearItemValues}
                            commonStore={commonStore}
                            providerStore={providerStore}
                            extractItemProperties={extractItemProperties}
                            isPageGroup={isPageGroup}
                            getItemControl={getItemControl}
                          />
                          {repeatCountVar > 0 && (
                            <IconButton
                              aria-label={`${childLinkId}-remove-button`}
                              onClick={() =>
                                handleRemoveGroup(childLinkId, child, value, getValues, setValue, errors, clearErrors)
                              }
                              sx={{ position: 'absolute', top: -10, right: -10 }}
                              id={`${childLinkId}-repeat-button`}
                            >
                              <CloseIcon id={`${childLinkId}-repeat-close-icon-${value}`} />
                            </IconButton>
                          )}
                        </Grid>
                      ))}
                      <Typography
                        variant="h6"
                        sx={{ m: isGroup_a_Page ? '1.25rem 0' : '0 0 1.25rem 0' }}
                        id={`typography-repeat-button-description-${childLinkId}`}
                      >
                        If more than one {child?.text?.toLowerCase() || ''} involved:
                      </Typography>
                      <Button
                        id={`group-repeat-${childLinkId}`}
                        onClick={() => handleAddGroup(childLinkId)}
                        disabled={maxRepeatCount && maxRepeatCount - 1 < repeatCountVar + 1}
                        variant="outlined"
                      >
                        Add Another {child.text || 'Group'}
                      </Button>
                    </>
                  ) : (
                    <ItemRenderer
                      item={child}
                      formMethods={formMethods}
                      enableWhenOk={enableWhenOk}
                      parentId={parentId}
                      repeatCount={repeatCount}
                      groupCounts={groupCounts}
                      handleAddGroup={handleAddGroup}
                      handleRemoveGroup={handleRemoveGroup}
                      clearValues={clearValues}
                      clearItemValues={clearItemValues}
                      commonStore={commonStore}
                      providerStore={providerStore}
                      extractItemProperties={extractItemProperties}
                      isPageGroup={isPageGroup}
                      getItemControl={getItemControl}
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Box>
      );
    }

    default: {
      return null;
    } // Unsupported types are ignored (can be expanded later)
  }
};

ItemRenderer.propTypes = {
  repeatCount: PropTypes.number.isRequired,
  item: PropTypes.object.isRequired,
  formMethods: PropTypes.object.isRequired,
  enableWhenOk: PropTypes.func.isRequired,
  groupCounts: PropTypes.object.isRequired,
  handleAddGroup: PropTypes.func.isRequired,
  handleRemoveGroup: PropTypes.func.isRequired,
  clearValues: PropTypes.func.isRequired,
  clearItemValues: PropTypes.func.isRequired,
  commonStore: PropTypes.oneOf([CommonStore]),
  providerStore: PropTypes.oneOf([ProviderStore]),
  extractItemProperties: PropTypes.func.isRequired,
  isPageGroup: PropTypes.func.isRequired,
  getItemControl: PropTypes.func.isRequired,
};

export default inject('commonStore', 'providerStore')(observer(ItemRenderer));
